import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Hash, Plus, Send, Image as ImageIcon, X, Search,
  MessageSquare, Users, Bot, Sparkles,
} from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 3000;
const AI_BOT_USER_ID = "ai-tailor-bot";

const dmId = (a, b) => {
  const [x, y] = [a, b].sort();
  return `dm:${x}:${y}`;
};
const aiThreadId = (uid) => `ai:${uid}`;

const getMe = () => {
  try { return JSON.parse(sessionStorage.getItem("adminUser") || "null"); } catch { return null; }
};

const initialsOf = (name) =>
  (name || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const AdminMessagesPage = () => {
  const navigate = useNavigate();
  const me = useMemo(() => getMe(), []);
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [unread, setUnread] = useState({ by_conversation: {}, total: 0 });
  const [activeConv, setActiveConv] = useState(null); // {id, label, type, peer?}
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [pendingImage, setPendingImage] = useState(null);
  const [sending, setSending] = useState(false);
  const [aiTyping, setAiTyping] = useState(false);
  const [newChannelOpen, setNewChannelOpen] = useState(false);
  const [search, setSearch] = useState("");
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auth gate
  useEffect(() => {
    if (sessionStorage.getItem("adminAuth") !== "true" || !me) {
      navigate("/admin");
    }
  }, [me, navigate]);

  // Initial fetch of channels + users
  const loadChannelsUsers = useCallback(async () => {
    try {
      const [cRes, uRes] = await Promise.all([
        axios.get(`${API}/chat/channels`),
        axios.get(`${API}/chat/users`),
      ]);
      setChannels(cRes.data?.channels || []);
      setUsers((uRes.data?.users || []).filter((u) => u.id !== me?.id));
    } catch {
      toast.error("Failed to load chat data");
    }
  }, [me?.id]);

  useEffect(() => { loadChannelsUsers(); }, [loadChannelsUsers]);

  // Default to #general on first load
  useEffect(() => {
    if (!activeConv && channels.length > 0) {
      const gen = channels.find((c) => c.name === "general") || channels[0];
      setActiveConv({ id: gen.id, label: `#${gen.name}`, type: "channel" });
    }
  }, [channels, activeConv]);

  // Fetch messages + unread counts. Polls every POLL_MS.
  const refresh = useCallback(async () => {
    if (!me) return;
    try {
      const [u] = await Promise.all([
        axios.get(`${API}/chat/unread`, { params: { user_id: me.id } }),
      ]);
      setUnread(u.data || { by_conversation: {}, total: 0 });
      if (activeConv?.id) {
        const m = await axios.get(`${API}/chat/messages`, {
          params: { conversation_id: activeConv.id, limit: 200 },
        });
        setMessages(m.data?.messages || []);
      }
    } catch { /* keep silent */ }
  }, [me, activeConv?.id]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  // Auto-scroll to newest
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Mark conversation as read on open + after new messages arrive
  useEffect(() => {
    if (!activeConv?.id || !me) return;
    axios.post(`${API}/chat/read`, null, {
      params: { user_id: me.id, conversation_id: activeConv.id },
    }).catch(() => {});
  }, [activeConv?.id, me, messages.length]);

  const sendMessage = async () => {
    if (!activeConv?.id || !me) return;
    if (!draft.trim() && !pendingImage) return;
    const willTriggerAi =
      activeConv.id.startsWith("ai:") || /@ai\b/i.test(draft);
    try {
      setSending(true);
      if (willTriggerAi) setAiTyping(true);
      await axios.post(`${API}/chat/messages`, {
        conversation_id: activeConv.id,
        user_id: me.id,
        text: draft.trim(),
        image_b64: pendingImage || null,
      });
      setDraft("");
      setPendingImage(null);
      await refresh();
    } catch (e) {
      toast.error("Failed to send");
    } finally {
      setSending(false);
      setAiTyping(false);
    }
  };

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPendingImage(reader.result);
    reader.readAsDataURL(file);
  };

  const openDm = (user) => {
    if (!me) return;
    setActiveConv({
      id: dmId(me.id, user.id),
      label: user.full_name || user.username,
      type: "dm",
      peer: user,
    });
  };

  const openAiThread = () => {
    if (!me) return;
    setActiveConv({
      id: aiThreadId(me.id),
      label: "AI Tailor",
      type: "ai",
    });
  };

  const createChannel = async (name, description) => {
    try {
      const res = await axios.post(`${API}/chat/channels`, {
        name: name.toLowerCase().replace(/[^a-z0-9-_]/g, "-"),
        description: description || "",
        is_private: false,
        created_by: me?.id || "",
      });
      const ch = res.data?.channel;
      await loadChannelsUsers();
      if (ch?.id) setActiveConv({ id: ch.id, label: `#${ch.name}`, type: "channel" });
      setNewChannelOpen(false);
      toast.success("Channel created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create channel");
    }
  };

  const filteredChannels = channels.filter((c) =>
    !search || c.name.toLowerCase().includes(search.toLowerCase())
  );
  const filteredUsers = users.filter((u) => {
    const s = search.toLowerCase();
    return !s ||
      (u.full_name || "").toLowerCase().includes(s) ||
      (u.username || "").toLowerCase().includes(s);
  });

  return (
    <div className="min-h-screen pt-20">
      <section className="border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="msg-back-btn">
            <ArrowLeft size={14} className="mr-1" /> Admin
          </Button>
          <div className="flex items-center gap-2">
            <MessageSquare size={18} className="text-amber-400" />
            <h1 className="text-base font-medium">Messages</h1>
            {unread.total > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-medium rounded-full px-2 py-0.5" data-testid="msg-total-unread">
                {unread.total}
              </span>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-2 md:px-4 py-4">
        <div className="flex gap-3" style={{ height: "calc(100vh - 160px)" }}>
          {/* Sidebar */}
          <aside className="w-64 bg-card border border-white/10 rounded-lg overflow-hidden flex flex-col">
            <div className="p-2 border-b border-white/5">
              <div className="relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-7 h-8 text-xs"
                  data-testid="msg-search"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* AI Tailor pinned */}
              <div className="p-2">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Assistant</p>
                {(() => {
                  const aid = aiThreadId(me?.id || "");
                  const isActive = activeConv?.id === aid;
                  const n = unread.by_conversation?.[aid] || 0;
                  return (
                    <button
                      onClick={openAiThread}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${
                        isActive
                          ? "bg-gradient-to-r from-amber-500/20 to-purple-500/20 text-amber-300 ring-1 ring-amber-500/30"
                          : "hover:bg-muted/40 text-foreground"
                      }`}
                      data-testid="msg-ai-tailor-btn"
                    >
                      <span className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center shrink-0">
                        <Sparkles size={10} className="text-white" />
                      </span>
                      <span className="flex-1 text-left truncate font-medium">AI Tailor</span>
                      {n > 0 && (
                        <span className="bg-red-500 text-white text-[9px] font-medium rounded-full px-1.5 min-w-[18px] text-center">
                          {n}
                        </span>
                      )}
                    </button>
                  );
                })()}
                <p className="text-[9px] text-muted-foreground/70 mt-1 px-2 leading-tight">
                  Tip: type <span className="text-amber-400 font-mono">@ai</span> in any channel or DM
                </p>
              </div>

              {/* Channels */}
              <div className="p-2">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Channels</p>
                  <Button
                    size="sm" variant="ghost"
                    className="h-5 w-5 p-0"
                    onClick={() => setNewChannelOpen(true)}
                    data-testid="msg-new-channel-btn"
                  >
                    <Plus size={12} />
                  </Button>
                </div>
                {filteredChannels.map((c) => {
                  const isActive = activeConv?.id === c.id;
                  const n = unread.by_conversation?.[c.id] || 0;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setActiveConv({ id: c.id, label: `#${c.name}`, type: "channel" })}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
                        isActive ? "bg-amber-500/10 text-amber-400" : "hover:bg-muted/40 text-foreground"
                      }`}
                      data-testid={`msg-channel-${c.name}`}
                    >
                      <Hash size={12} />
                      <span className="flex-1 text-left truncate">{c.name}</span>
                      {n > 0 && (
                        <span className="bg-red-500 text-white text-[9px] font-medium rounded-full px-1.5 min-w-[18px] text-center">
                          {n}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Direct messages */}
              <div className="p-2">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                  <Users size={10} /> Direct Messages
                </p>
                {filteredUsers.length === 0 && (
                  <p className="text-[10px] text-muted-foreground italic">No other users yet</p>
                )}
                {filteredUsers.map((u) => {
                  const tid = dmId(me?.id || "", u.id);
                  const isActive = activeConv?.id === tid;
                  const n = unread.by_conversation?.[tid] || 0;
                  return (
                    <button
                      key={u.id}
                      onClick={() => openDm(u)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
                        isActive ? "bg-amber-500/10 text-amber-400" : "hover:bg-muted/40 text-foreground"
                      }`}
                      data-testid={`msg-dm-${u.username}`}
                    >
                      <span className="w-5 h-5 rounded-full bg-muted/50 flex items-center justify-center text-[9px] font-medium">
                        {initialsOf(u.full_name || u.username)}
                      </span>
                      <span className="flex-1 text-left truncate">{u.full_name || u.username}</span>
                      {n > 0 && (
                        <span className="bg-red-500 text-white text-[9px] font-medium rounded-full px-1.5 min-w-[18px] text-center">
                          {n}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Chat pane */}
          <div className="flex-1 bg-card border border-white/10 rounded-lg overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
              {activeConv ? (
                <>
                  {activeConv.type === "channel" ? <Hash size={14} />
                    : activeConv.type === "ai" ? <Bot size={14} className="text-amber-400" />
                    : <Users size={14} />}
                  <p className="text-sm font-medium" data-testid="msg-active-title">{activeConv.label}</p>
                  {activeConv.type === "ai" && (
                    <span className="text-[10px] text-muted-foreground ml-2">Private AI thread · with full DB access</span>
                  )}
                </>
              ) : (
                <p className="text-xs text-muted-foreground">Pick a conversation</p>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="msg-list">
              {messages.length === 0 && (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                  No messages yet — say hi!
                </div>
              )}
              {messages.map((m) => {
                const mine = m.user_id === me?.id;
                const isBot = m.user_id === AI_BOT_USER_ID;
                // Parse [shared:Label]\n...\n[/shared] block
                const sharedMatch = m.text && m.text.match(/\[shared:([^\]]+)\]\n([\s\S]*?)\n\[\/shared\]/);
                const prefixText = sharedMatch
                  ? m.text.slice(0, sharedMatch.index).trim()
                  : m.text;
                const sharedLabel = sharedMatch?.[1];
                const sharedContent = sharedMatch?.[2];
                return (
                  <motion.div
                    key={m.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex gap-2 ${mine ? "justify-end" : "justify-start"}`}
                    data-testid="msg-bubble"
                  >
                    {!mine && (
                      isBot ? (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center text-[10px] font-medium shrink-0 mt-1" data-testid="msg-bot-avatar">
                          <Sparkles size={12} className="text-white" />
                        </div>
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-muted/40 flex items-center justify-center text-[10px] font-medium shrink-0 mt-1">
                          {initialsOf(m.user_name)}
                        </div>
                      )
                    )}
                    <div className={`max-w-[70%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                      {!mine && (
                        <span className={`text-[10px] mb-0.5 ${isBot ? "text-amber-400 font-medium" : "text-muted-foreground"}`}>
                          {isBot ? "AI Tailor" : m.user_name}
                        </span>
                      )}
                      <div className={`rounded-lg px-3 py-2 text-sm ${
                        mine
                          ? "bg-amber-500 text-black"
                          : isBot
                            ? "bg-gradient-to-br from-amber-500/10 to-purple-500/10 ring-1 ring-amber-500/20 text-foreground"
                            : "bg-muted/40"
                      }`}>
                        {m.image_b64 && (
                          <img
                            src={m.image_b64}
                            alt=""
                            className="rounded mb-1 max-h-60 w-auto"
                          />
                        )}
                        {prefixText && <p className="whitespace-pre-wrap break-words">{prefixText}</p>}
                        {sharedMatch && (
                          <div className="mt-2 border-l-2 border-amber-400/60 bg-amber-500/5 rounded-r px-2 py-1.5" data-testid="msg-shared-card">
                            <div className="flex items-center gap-1 text-[10px] text-amber-400 mb-1">
                              <Sparkles size={10} /> Shared from {sharedLabel}
                            </div>
                            <p className="text-xs whitespace-pre-wrap break-words opacity-90">{sharedContent}</p>
                          </div>
                        )}
                      </div>
                      <span className="text-[9px] text-muted-foreground mt-0.5">
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
              {aiTyping && (
                <div className="flex gap-2 justify-start" data-testid="msg-ai-typing">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center shrink-0 mt-1">
                    <Sparkles size={12} className="text-white animate-pulse" />
                  </div>
                  <div className="bg-gradient-to-br from-amber-500/10 to-purple-500/10 ring-1 ring-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-400 italic">
                    AI Tailor is thinking…
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Composer */}
            <div className="p-3 border-t border-white/5">
              {pendingImage && (
                <div className="relative inline-block mb-2">
                  <img src={pendingImage} alt="" className="h-16 rounded border border-white/10" />
                  <button
                    onClick={() => setPendingImage(null)}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center"
                    data-testid="msg-remove-image"
                  >
                    <X size={10} />
                  </button>
                </div>
              )}
              <div className="flex items-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!activeConv || sending}
                  data-testid="msg-attach-btn"
                >
                  <ImageIcon size={14} />
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleImage}
                />
                <textarea
                  rows={1}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder={activeConv
                    ? activeConv.type === "ai"
                      ? "Ask AI Tailor… (fabrics, orders, sizing, styling)"
                      : `Message ${activeConv.label} — tip: @ai to ask the AI`
                    : "Pick a conversation first"}
                  disabled={!activeConv || sending}
                  className="flex-1 resize-none bg-background border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-amber-500 disabled:opacity-50"
                  data-testid="msg-composer"
                />
                <Button
                  size="sm"
                  onClick={sendMessage}
                  disabled={!activeConv || sending || (!draft.trim() && !pendingImage)}
                  data-testid="msg-send-btn"
                >
                  <Send size={14} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* New channel dialog */}
      <NewChannelDialog
        open={newChannelOpen}
        onOpenChange={setNewChannelOpen}
        onCreate={createChannel}
      />
    </div>
  );
};

const NewChannelDialog = ({ open, onOpenChange, onCreate }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  useEffect(() => {
    if (!open) { setName(""); setDescription(""); }
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Channel</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs text-muted-foreground">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. fabric-questions"
              data-testid="new-channel-name"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Description (optional)</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this channel for?"
              data-testid="new-channel-description"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => onCreate(name.trim(), description.trim())}
            disabled={!name.trim()}
            data-testid="new-channel-create-btn"
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminMessagesPage;
